import React, { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Layout from "../components/Layout";
import RequireAuth from "../components/RequireAuth";
import Home from "../routes/Home";
import AboutMe from "../routes/AboutMe";
import Contact from "../routes/Contact";
import Experiences from "../routes/Experiences";
import Login from "../routes/Login";
import NewContent from "../routes/NewContent";
import Projects from "../routes/Projects";
import TechNews from "../routes/TechNews";
import ProjectDetails from "../routes/ProjectDetails";
import PostAnArticle from "../routes/PostAnArticle";
import PrivacyPolicy from "../routes/PrivacyPolicy";
import TermsOfUse from "../routes/TermsOfUse";
import EditorialPolicy from "../routes/EditorialPolicy";
import ArticleList from "../components/ArticleList";
import ArticlePage from "../components/ArticlePage";
import OldAlexHub from "../routes/OldAlexHub";
import AdminDashboard from "../routes/AdminDashboard";
import { shouldTrackPath } from "../utils/acquisition";
import {
  flushVisitorEngagement,
  trackVisitor,
} from "../utils/visitorTracking";

const NtdIntelligence = React.lazy(() => import("../routes/NtdIntelligence"));

const TrackingWrapper = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    if (!shouldTrackPath(location.pathname)) {
      return undefined;
    }

    const pageUrl = `${location.pathname}${location.search}`;
    let isActive = true;
    let activeSince =
      typeof document !== "undefined" && document.visibilityState === "hidden"
        ? null
        : Date.now();

    const syncVisitor = async () => {
      try {
        const trackingData = await trackVisitor(pageUrl);

        if (isActive && trackingData?.summary) {
          window.dispatchEvent(
            new CustomEvent("visitor-summary-updated", {
              detail: trackingData.summary,
            })
          );
        }
      } catch (error) {
        console.error("Error syncing visitor data:", error);
      }
    };

    const flushEngagement = async (keepalive = false) => {
      if (activeSince === null) {
        return;
      }

      const engagementMs = Date.now() - activeSince;
      activeSince = null;

      try {
        await flushVisitorEngagement({
          pageUrl,
          engagementMs,
          keepalive,
        });
      } catch (error) {
        console.error("Error syncing visitor engagement:", error);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        void flushEngagement(true);
      } else if (activeSince === null) {
        activeSince = Date.now();
      }
    };

    const handlePageHide = () => {
      void flushEngagement(true);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    syncVisitor();

    return () => {
      isActive = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      void flushEngagement();
    };
  }, [location.pathname, location.search]);

  return children;
};

// Main Route Manager
const RouteManager = () => {
  return (
    <BrowserRouter>
      <TrackingWrapper>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="aboutme" element={<AboutMe />} />
            <Route path="contactus" element={<Contact />} />
            <Route path="experiences" element={<Experiences />} />
            <Route path="login" element={<Login />} />
            <Route path="newcontent" element={<NewContent />} />
            <Route
              path="dashboard"
              element={
                <RequireAuth>
                  <AdminDashboard />
                </RequireAuth>
              }
            />
            <Route path="projects" element={<Projects />} />
            <Route
              path="projects/ntd-intelligence"
              element={
                <React.Suspense fallback={<div className="container py-5" role="status">Loading NTD Intelligence…</div>}>
                  <NtdIntelligence />
                </React.Suspense>
              }
            />
            <Route path="projects/:projectId" element={<ProjectDetails />} />
            <Route path="technews" element={<TechNews />} />
            <Route path="oldalexhub" element={<OldAlexHub />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-of-use" element={<TermsOfUse />} />
            <Route path="editorial-policy" element={<EditorialPolicy />} />
            <Route
              path="postanarticle"
              element={
                <RequireAuth>
                  <PostAnArticle />
                </RequireAuth>
              }
            />
            <Route
              path="visitor-insights"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route path="/articlelist" element={<ArticleList />} />
            <Route path="/articles/:slug" element={<ArticlePage />} />
          </Route>
        </Routes>
      </TrackingWrapper>
    </BrowserRouter>
  );
};

export default RouteManager;
